import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Icons } from '../ui/Icons';
import { Reveal } from '../ui/Reveal';

type Answer = 'yes' | 'unsure' | 'no' | null;

const OPTIONS: { id: Exclude<Answer, null>; label: string }[] = [
  { id: 'yes', label: 'Yes' },
  { id: 'unsure', label: 'Not sure' },
  { id: 'no', label: 'No' },
];

const QUESTIONS = [
  'Is your parent on a Medicare Advantage plan?',
  'Has there been a recent fall or change in mobility?',
];

export function MedicareWidget() {
  const { openLeadForm } = useApp();
  const [answers, setAnswers] = useState<[Answer, Answer]>([null, null]);
  const bothAnswered = answers[0] !== null && answers[1] !== null;

  const setAnswer = (i: number, value: Exclude<Answer, null>) =>
    setAnswers((prev) => {
      const next = [...prev] as [Answer, Answer];
      next[i] = value;
      return next;
    });

  return (
    <section id="medicare" className="relative py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0 bg-blush" />
      <div className="pointer-events-none absolute -right-24 top-16 h-80 w-80 rounded-full bg-sage/10 blur-3xl" />

      <div className="container-rosa relative grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
        {/* Left — editorial copy */}
        <Reveal>
          <p className="eyebrow">Good to know</p>
          <h2 className="mt-4 font-display text-[2.1rem] font-bold leading-[1.08] tracking-[-0.01em] text-plum sm:text-4xl">
            Some of this may be covered.
          </h2>
          <p className="mt-5 max-w-xl font-body text-lg leading-relaxed text-plum/75">
            Since 2019, some Medicare Advantage plans can help cover home safety changes under a benefit called SSBCI.
            Coverage varies by plan, and there is never a guarantee, but a CAPS-certified specialist can help you check
            exactly what a plan includes.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/8 px-4 py-1.5">
            <Icons.shield width={17} height={17} className="text-sage-deep" />
            <span className="font-body text-[13px] font-semibold text-sage-deep">Honest guidance, never a promise of coverage</span>
          </div>
        </Reveal>

        {/* Right — interactive checker */}
        <Reveal delay={0.1}>
          <div className="rounded-card border border-stone bg-white p-6 shadow-soft sm:p-7">
            <p className="flex items-center gap-2 font-display text-lg font-bold text-plum">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-terracotta/12 text-terracotta">
                <Icons.sparkle width={20} height={20} />
              </span>
              Could a Medicare Advantage plan help cover it?
            </p>

            <div className="mt-6 space-y-6">
              {QUESTIONS.map((q, i) => (
                <div key={q}>
                  <p className="font-body text-[15px] font-semibold text-plum">{q}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {OPTIONS.map((opt) => {
                      const active = answers[i] === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setAnswer(i, opt.id)}
                          aria-pressed={active}
                          className={`cursor-pointer rounded-full border px-5 py-2 font-body text-sm font-semibold transition-all duration-200 ${
                            active
                              ? 'border-terracotta bg-terracotta text-blush shadow-soft'
                              : 'border-stone bg-white text-plum/75 hover:border-terracotta/40 hover:text-plum'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <AnimatePresence>
              {bothAnswered && (
                <motion.div
                  initial={{ opacity: 0, y: 12, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: 12, height: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-6 rounded-2xl border border-sage/25 bg-sage/8 p-5">
                    <div className="flex items-start gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sage/20 text-sage-deep">
                        <Icons.check width={22} height={22} />
                      </span>
                      <div>
                        <p className="font-display text-lg font-bold text-plum">It&rsquo;s worth checking.</p>
                        <p className="mt-1 font-body text-[15px] leading-relaxed text-plum/75">
                          Plans differ, but many now include a home safety benefit. Your CAPS-certified specialist can walk
                          you through what yours covers, at no cost to you.
                        </p>
                      </div>
                    </div>
                    <button onClick={() => openLeadForm()} className="btn-primary mt-5 w-full">
                      Get a free safety assessment <Icons.arrowRight width={18} height={18} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
